import React, { Fragment } from "react";

import img13 from "../../images/service/businessConsultant/business consulting.png";
import img14 from "../../images/service/businessConsultant/business consulting (2).png";
import img15 from "../../images/service/businessConsultant/business consulting (3).png";

// Service icons
import sIcon1 from "../../images/icon/service-icon01.svg";
import sIcon2 from "../../images/icon/service-icon02.svg";
import sIcon3 from "../../images/icon/service-icon03.svg";
import sIcon4 from "../../images/icon/service-icon04.svg";
import sIcon5 from "../../images/icon/service-icon05.svg";
import sIcon6 from "../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";

import HeroSection from "../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../components/PageSection/ServiceCardSection";
import Benefit from "../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../components/PageSection/FinalCTA";
import Industries from "../../components/Industries/Industries";
import TheorySection from "../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Growth & Market Strategy",
    content:
      "Market expansion, customer segmentation, pricing optimization, go-to-market planning, competitive positioning.",
  },
  {
    icon: sIcon2,
    title: "Operational Excellence",
    content:
      "Process optimization, cost reduction, supply chain efficiency, performance management systems, lean implementation.",
  },
  {
    icon: sIcon3,
    title: "Digital & Technology Transformation",
    content:
      "Technology roadmap, AI & automation adoption, data strategy, digital customer experience improvement.",
  },
  {
    icon: sIcon4,
    title: "Leadership & Organization",
    content:
      "Executive coaching, team alignment, culture change, talent strategy, change management programs.",
  },
  {
    icon: sIcon5,
    title: "Risk, Compliance & ESG",
    content:
      "Risk assessment, regulatory readiness, sustainability strategy, business continuity planning.",
  },
  {
    icon: sIcon6,
    title: "Performance & Analytics",
    content:
      "KPI frameworks, dashboards, data-driven decision making, forecasting & scenario planning.",
  },
];

const benefits = [
  {
    title: "20–55% Efficiency Improvement",
    description:
      "Improvement in operational efficiency or cost structure through streamlined processes and strategic optimization.",
    icon: sIcon1,
  },
  {
    title: "2–5× Faster Execution",
    description:
      "Accelerated execution speed on strategic initiatives with clear priorities and focused action plans.",
    icon: sIcon2,
  },
  {
    title: "15–45% Revenue Growth",
    description:
      "Revenue or profitability improvement within 12–24 months through smart strategy and effective implementation.",
    icon: sIcon3,
  },
  {
    title: "Sustainable Competitive Advantage",
    description:
      "Build lasting competitive edge through focused strategy, smarter operations, and continuous performance improvement.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What is strategic business consulting?",
    a: "Strategic business consulting helps organizations create clarity, accelerate performance, and achieve sustainable competitive advantage through focused strategy, smarter operations, and effective execution.",
  },
  {
    q: "Why do businesses need consulting in 2026?",
    a: "Economic uncertainty, rapid technology adoption, talent shortages, sustainability expectations, and shifting customer behavior require companies to make faster, better-informed decisions with external perspective and specialized expertise.",
  },
  {
    q: "What results can I expect from business consulting?",
    a: "Our clients typically achieve 20–55% improvement in operational efficiency, 2–5× faster execution on strategic initiatives, and 15–45% revenue or profitability improvement within 12–24 months.",
  },
  {
    q: "How is your consulting approach different?",
    a: "We focus on measurable business outcomes, not just reports. Our approach combines deep discovery, strategic prioritization, and hands-on execution support with knowledge transfer for sustained success.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function BusinessConsulting() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Business Consulting"
        pagesub="Strategic guidance to accelerate performance and achieve competitive advantage"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            <span className="gradient-text">Strategic Business Consulting</span>{" "}
            for Growth Leaders
          </>
        }
        subtitle={`We partner with ambitious leaders and growing organizations to create clarity, 
accelerate performance, and achieve sustainable competitive advantage through focused strategy, 
smarter operations, and effective execution.`}
        buttonText="Schedule Strategy Call"
        buttonLink="/contact"
        image={img13}
        imageAlt="Strategic Business Consulting"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Businesses Need Strategic Guidance In 2026"
        image={img14}
        paragraphs={[
          "Economic uncertainty, rapid technology adoption, talent shortages, sustainability expectations, and shifting customer behavior require companies to make faster, better-informed decisions — often with external perspective and specialized expertise.",
          "Growth pressure with rising revenue targets, operational complexity from hybrid work and supply chain volatility, and increasing risk & regulation demands require proactive strategic planning and execution.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Our Core Consulting Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Our Structured Consulting Approach"
        image={img15}
        paragraphs={[
          "Deep Discovery: We understand your goals, challenges, key metrics, market position, and internal capabilities through structured diagnostics.",
          "Strategy & Prioritization: Co-create focused, high-impact plans with clear priorities, timelines, responsibilities, and success measures.",
          "Execution & Results: Provide hands-on support during implementation, track progress, adjust tactics, and ensure knowledge transfer for sustained success.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Results Our Clients Typically Achieve"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Challenges We Address"
        subtitle="Solving critical business challenges with expert guidance and proven methodologies"
        features={[
          {
            icon: sIcon1,
            title: "Growth Pressure",
            description:
              "Revenue targets are rising while acquisition costs increase — smart strategy is essential for sustainable growth.",
          },
          {
            icon: sIcon2,
            title: "Operational Complexity",
            description:
              "Hybrid work, supply chain volatility, and cost inflation demand streamlined processes and efficiency.",
          },
          {
            icon: sIcon3,
            title: "Risk & Regulation",
            description:
              "New compliance requirements and ESG expectations require proactive planning and risk management.",
          },
          {
            icon: sIcon4,
            title: "Technology Transformation",
            description:
              "Navigate AI adoption, automation, and digital transformation with clear roadmaps and implementation support.",
          },
          {
            icon: sIcon5,
            title: "Leadership & Culture",
            description:
              "Build strong teams, align leadership, and drive culture change for organizational effectiveness.",
          },
          {
            icon: sIcon6,
            title: "Performance Management",
            description:
              "Establish KPI frameworks and data-driven decision making for measurable business outcomes.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Results-Oriented Approach: We focus on measurable business outcomes — not just reports or hours billed",
          "Practical & Fast Execution: Clear, actionable recommendations designed for real-world implementation speed",
          "Cross-Industry Experience: Best practices from multiple sectors adapted to your unique situation",
          "Long-term Partnership Mindset: We build lasting relationships and transfer knowledge for sustained success",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
